import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { agentApi } from '../services/api';
import { CreateAgentRequest, EndCondition } from '../types';
import { useLoading } from '../context/LoadingContext';
import { useNotification } from '../context/NotificationContext';
import { useErrorHandler } from '../hooks/useErrorHandler';

// Available models
const MODELS = [
  { value: 'gpt-4o-2024-11-20', label: 'GPT-4o (OpenAI)' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (OpenAI)' },
  { value: 'claude-3-7-sonnet-20250219', label: 'Claude 3.7 Sonnet (Anthropic)' },
];

// End condition types
const END_CONDITION_TYPES = [
  { value: 'keyword', label: 'Keyword Detection' },
  { value: 'sentiment', label: 'Sentiment Analysis' },
  { value: 'goal', label: 'Goal Completion' },
  { value: 'custom', label: 'Custom Logic' },
];

const CreateAgent = () => {
  const [formData, setFormData] = useState<CreateAgentRequest>({
    name: '',
    initialPrompt: 'You are a helpful assistant.',
    model: 'gpt-4o-2024-11-20',
    endConditions: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { startLoading, stopLoading } = useLoading();
  const { showNotification } = useNotification();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.initialPrompt.trim()) {
      newErrors.initialPrompt = 'Initial prompt is required';
    }
    
    if (!formData.model) {
      newErrors.model = 'Model is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleModelChange = (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, model: e.target.value }));
    
    // Clear error when field is edited
    if (errors.model) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.model;
        return newErrors;
      });
    }
  };

  const [endConditionInput, setEndConditionInput] = useState({
    type: 'keyword',
    keyword: '',
    description: '',
  });

  const handleEndConditionTypeChange = (event: SelectChangeEvent<string>) => {
    setEndConditionInput(prev => ({
      ...prev,
      type: event.target.value,
    }));
  };

  const handleEndConditionInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEndConditionInput(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const addEndCondition = () => {
    if (!endConditionInput.description) {
      showNotification('End condition description is required', 'error');
      return;
    }

    if (endConditionInput.type === 'keyword' && !endConditionInput.keyword) {
      showNotification('Keyword is required for keyword detection', 'error');
      return;
    }

    // Create the end condition with the correct structure
    const newEndCondition: EndCondition = {
      id: `ec-${Date.now()}`,
      type: endConditionInput.type as 'keyword' | 'sentiment' | 'goal' | 'custom',
      description: endConditionInput.description,
      parameters: {}
    };
    
    // Add keyword parameter only for keyword type
    if (endConditionInput.type === 'keyword') {
      newEndCondition.parameters.keyword = endConditionInput.keyword;
    }

    console.log('Adding end condition:', newEndCondition);

    setFormData(prev => ({
      ...prev,
      endConditions: [...(prev.endConditions || []), newEndCondition],
    }));

    // Reset input fields
    setEndConditionInput({
      type: 'keyword',
      keyword: '',
      description: '',
    });
  };

  const removeEndCondition = (id: string) => {
    setFormData(prev => ({
      ...prev,
      endConditions: prev.endConditions?.filter(ec => ec.id !== id) || [],
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || isSubmitting) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      startLoading();
      
      // Create a copy of the form data to ensure proper structure
      const requestData = {
        ...formData,
        endConditions: formData.endConditions?.map(condition => ({
          id: condition.id,
          type: condition.type,
          description: condition.description,
          parameters: condition.parameters || {}
        }))
      };
      
      console.log('Submitting agent with data:', requestData);
      const response = await agentApi.createAgent(requestData);
      showNotification(`Agent "${response.name}" created successfully`, 'success');
      navigate(`/agents/${response.agentId}`);
    } catch (error) {
      handleError(error);
      setSubmitError('Failed to create agent. There might be a connection issue with the backend server.');
      console.error('Failed to create agent:', error);
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };
  
  const handleRetry = () => {
    setSubmitError(null);
    handleSubmit(new Event('submit') as unknown as React.FormEvent);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Create New Agent
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Agent Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
          
          <FormControl fullWidth margin="normal" error={!!errors.model}>
            <InputLabel id="model-label">Model</InputLabel>
            <Select
              labelId="model-label"
              id="model"
              name="model"
              value={formData.model}
              onChange={handleModelChange}
              label="Model"
            >
              {MODELS.map((model) => (
                <MenuItem key={model.value} value={model.value}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
            {errors.model && <FormHelperText>{errors.model}</FormHelperText>}
          </FormControl>
          
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            End Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define conditions that will determine when the agent should stop processing
          </Typography>
          
          <Box sx={{ border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Add End Condition
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="end-condition-type-label">Type</InputLabel>
              <Select
                labelId="end-condition-type-label"
                id="end-condition-type"
                value={endConditionInput.type}
                onChange={handleEndConditionTypeChange}
                label="Type"
              >
                {END_CONDITION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {endConditionInput.type === 'keyword' && (
              <TextField
                margin="normal"
                fullWidth
                id="keyword"
                label="Keyword"
                name="keyword"
                value={endConditionInput.keyword}
                onChange={handleEndConditionInputChange}
                helperText="Enter a keyword that will trigger the end condition"
              />
            )}
            
            <TextField
              margin="normal"
              fullWidth
              id="description"
              label="Description"
              name="description"
              value={endConditionInput.description}
              onChange={handleEndConditionInputChange}
              helperText="Describe this end condition"
            />
            
            <Button
              variant="outlined"
              onClick={addEndCondition}
              sx={{ mt: 2 }}
            >
              Add Condition
            </Button>
          </Box>
          
          {(formData.endConditions?.length || 0) > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Defined End Conditions
              </Typography>
              
              {formData.endConditions?.map((condition) => (
                <Paper key={condition.id} sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2">
                      {END_CONDITION_TYPES.find(t => t.value === condition.type)?.label || condition.type}
                    </Typography>
                    <Typography variant="body2">
                      {condition.description}
                    </Typography>
                    {condition.type === 'keyword' && (
                      <Typography variant="body2" color="text.secondary">
                        Keyword: {condition.parameters.keyword}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeEndCondition(condition.id)}
                  >
                    Remove
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="initialPrompt"
            label="Initial Prompt"
            name="initialPrompt"
            value={formData.initialPrompt}
            onChange={handleChange}
            multiline
            rows={4}
            error={!!errors.initialPrompt}
            helperText={errors.initialPrompt || "Define how your agent should behave"}
          />
          
          {submitError && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography color="error" gutterBottom>
                {submitError}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRetry}
                size="small"
              >
                Retry
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/agents')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Agent'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateAgent;
